<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import Conexoes from '../../Classes/Conexoes.js';
  import Constantes from '../../Classes/Constantes.js';
  import { tenantLogado } from '../../Classes/Stores.js';

  const dispatch = createEventDispatcher();
  const conexao = new Conexoes();
  
  let tenant;
  $: tenant = $tenantLogado;
  
  // Variáveis para gerir os locais e o contexto selecionado
  let locais = [];
  let localSelecionado = 'todos'; // 'todos' ou o ID do local
  let configGlobal = {}; // Guarda a configuração padrão do tenant

  // Campos do formulário
  let configEmail = 'obrigatorio';
  let configCpf = 'oculto';
  let configNascimento = 'oculto';
  let configEndereco = 'oculto';
  let configObservacoes = 'oculto';
  let labelObservacoes = 'Observações';

  let dadosCarregados = false;

  onMount(async () => {
    if (!dadosCarregados && tenant && tenant.id) {
      await carregarDados();
    }
  });

  $: if (!dadosCarregados && tenant && tenant.id) {
    carregarDados();
  }

  async function carregarDados() {
    dispatch('loading', true);
    const url = Constantes.api_acesso.replace('acesso.php', 'obter_config_campos.php');
    const res = await conexao.conecta(url, { body: { id_tenant: tenant.id } });
    
    if (res && res.sucesso) {
      configGlobal = res.config_global || {};
      locais = res.locais || [];
      aplicarValoresNoFormulario();
    }
    dadosCarregados = true;
    dispatch('loading', false);
  }

  // Atualiza os selects visuais dependendo de qual local está selecionado no dropdown
  function aplicarValoresNoFormulario() {
    const global = configGlobal || {};

    if (localSelecionado === 'todos') {
      configEmail = global.campo_email || 'obrigatorio';
      configCpf = global.campo_cpf || 'oculto';
      configNascimento = global.campo_nascimento || 'oculto';
      configEndereco = global.campo_endereco || 'oculto';
      configObservacoes = global.campo_observacoes || 'oculto';
      labelObservacoes = global.label_observacoes || 'Observações';
    } else {
      const loc = locais.find(l => l.id.toString() === localSelecionado);
      // Se o local já tiver config própria, usa-a. Se for null (novo local), herda o global para visualização
      configEmail = (loc && loc.campo_email) || global.campo_email || 'obrigatorio';
      configCpf = (loc && loc.campo_cpf) || global.campo_cpf || 'oculto';
      configNascimento = (loc && loc.campo_nascimento) || global.campo_nascimento || 'oculto';
      configEndereco = (loc && loc.campo_endereco) || global.campo_endereco || 'oculto';
      configObservacoes = (loc && loc.campo_observacoes) || global.campo_observacoes || 'oculto';
      labelObservacoes = (loc && loc.label_observacoes) || global.label_observacoes || 'Observações';
    }
  }

  async function salvarConfiguracoes() {
    dispatch('loading', true);
    
    const url = Constantes.api_acesso.replace('acesso.php', 'salvar_config_campos.php');
    const res = await conexao.conecta(url, {
      body: {
        id_tenant: tenant.id,
        id_local: localSelecionado, // Passa 'todos' ou o ID específico
        campo_email: configEmail,
        campo_cpf: configCpf,
        campo_nascimento: configNascimento,
        campo_endereco: configEndereco,
        campo_observacoes: configObservacoes,
        label_observacoes: labelObservacoes
      }
    });
    
    dispatch('loading', false);
    
    if (res && res.sucesso) {
      // Atualiza a store se foi alteração global
      if (res.tenant) tenantLogado.set(res.tenant);
      alert(res.mensagem);
      await carregarDados(); // Recarrega para ter a certeza que a memória local está correta
    } else {
      alert("Erro ao salvar configurações do formulário.");
    }
  }
</script>

<section class="card-panel fade-in">
  <h2>Personalizar Formulário de Agendamento</h2>
  <p class="text-muted mb-4">Escolha quais dados deseja recolher dos seus clientes no momento da reserva.</p>

  <div class="context-selector mb-4">
    <label for="localSelect"><i class="fas fa-map-marker-alt text-accent"></i> Configurar campos para:</label>
    <select id="localSelect" class="input-app" bind:value={localSelecionado} on:change={aplicarValoresNoFormulario}>
      <option value="todos">Todos os Locais (Configuração Padrão)</option>
      {#if locais.length > 0}
        <optgroup label="Locais Específicos">
          {#each locais as loc}
            <option value={loc.id.toString()}>{loc.nome} {loc.campo_email ? '⚙️' : ''}</option>
          {/each}
        </optgroup>
      {/if}
    </select>
    <small class="text-muted mt-1" style="display:block;">
      {localSelecionado === 'todos' 
        ? 'Ao salvar, isto irá sobrepor as configurações de todos os locais.' 
        : 'As configurações salvas agora serão aplicadas apenas a este local específico.'}
    </small>
  </div>

  <div class="fields-settings-grid">
    <div class="setting-box locked">
      <div class="box-info">
        <span class="badge-lock"><i class="fas fa-lock"></i> Fixo Obrigatório</span>
        <h4>Nome Completo</h4>
        <p class="text-muted">Essencial para identificação básica na sua agenda.</p>
      </div>
    </div>

    <div class="setting-box locked">
      <div class="box-info">
        <span class="badge-lock"><i class="fas fa-lock"></i> Fixo Obrigatório</span>
        <h4>Telefone / WhatsApp</h4>
        <p class="text-muted">Essencial para o envio de alertas SMS e contacto emergencial.</p>
      </div>
    </div>

    <div class="setting-box special-email">
      <div class="box-info">
        <h4>E-mail do Cliente</h4>
        <p class="text-muted">Selecione a visibilidade deste campo.</p>
        
        <div class="email-alert-box mt-2">
          <i class="fas fa-exclamation-triangle"></i>
          <span><strong>Atenção profissional:</strong> O e-mail é estritamente necessário para que o motor de envios dispare os links de confirmação e os lembretes automáticos. Se ocultado, o cliente não receberá alertas.</span>
        </div>
      </div>
      <div class="box-action">
        <select class="input-app" bind:value={configEmail}>
          <option value="obrigatorio">Obrigatório (Recomendado)</option>
          <option value="opcional">Opcional</option>
          <option value="oculto">Oculto / Não solicitar</option>
        </select>
      </div>
    </div>

    <div class="setting-box">
      <div class="box-info">
        <h4>CPF</h4>
        <p class="text-muted">Útil para prontuários, emissão de recibos ou faturas.</p>
      </div>
      <div class="box-action">
        <select class="input-app" bind:value={configCpf}>
          <option value="oculto">Oculto / Não solicitar</option>
          <option value="opcional">Opcional</option>
          <option value="obrigatorio">Obrigatório</option>
        </select>
      </div>
    </div>

    <div class="setting-box">
      <div class="box-info">
        <h4>Data de Nascimento</h4>
        <p class="text-muted">Importante para cálculos automáticos de idade em anamneses.</p>
      </div>
      <div class="box-action">
        <select class="input-app" bind:value={configNascimento}>
          <option value="oculto">Oculto / Não solicitar</option>
          <option value="opcional">Opcional</option>
          <option value="obrigatorio">Obrigatório</option>
        </select>
      </div>
    </div>

    <div class="setting-box">
      <div class="box-info">
        <h4>Endereço Completo</h4>
        <p class="text-muted">Necessário caso realize atendimentos domiciliares (Home Care).</p>
      </div>
      <div class="box-action">
        <select class="input-app" bind:value={configEndereco}>
          <option value="oculto">Oculto / Não solicitar</option>
          <option value="opcional">Opcional</option>
          <option value="obrigatorio">Obrigatório</option>
        </select>
      </div>
    </div>

    <div class="setting-box">
      <div class="box-info">
        <h4>{labelObservacoes}</h4>
        <p class="text-muted">Campo de observações enviado no formulário de agendamento.</p>
        <input class="input-app" type="text" bind:value={labelObservacoes} placeholder="Observações" />
      </div>
      <div class="box-action">
        <select class="input-app" bind:value={configObservacoes}>
          <option value="oculto">Oculto / Não solicitar</option>
          <option value="opcional">Opcional</option>
          <option value="obrigatorio">Obrigatório</option>
        </select>
      </div>
    </div>
  </div>

  <button class="btn-primary mt-4 px-5" on:click={salvarConfiguracoes}>
    <i class="fas fa-save me-2"></i> {localSelecionado === 'todos' ? 'Salvar Estrutura para Todos' : 'Salvar Estrutura Específica'}
  </button>
</section>

<style>
  .context-selector { background: #f8faf9; padding: 20px; border-radius: 12px; border: 1px solid #e0e6e2; max-width: 800px; }
  .context-selector label { font-weight: 700; font-size: 1.05rem; display: block; margin-bottom: 10px; color: var(--text-main); }
  .text-accent { color: #cfaa6c; }
  
  .fields-settings-grid { display: flex; flex-direction: column; gap: 15px; max-width: 800px; text-align: left; }
  .setting-box { background: white; border: 1px solid var(--border); padding: 20px; border-radius: 12px; display: flex; justify-content: space-between; align-items: center; gap: 20px; }
  .setting-box.locked { background: #f8faf9; border-style: dashed; }
  .setting-box h4 { margin: 0 0 5px 0; color: var(--text-main); font-size: 1.1rem; font-weight: 700; }
  .setting-box p { margin: 0; font-size: 0.85rem; }
  
  .badge-lock { font-size: 0.75rem; background: #cbd5e0; color: #4a5568; padding: 2px 8px; border-radius: 4px; font-weight: 700; display: inline-block; margin-bottom: 5px; }
  .box-action select { min-width: 220px; }

  .special-email { border-left: 4px solid var(--accent); flex-direction: column; align-items: stretch; gap: 10px; }
  .email-alert-box { background: #fffaf0; border: 1px solid #feebc8; padding: 12px 15px; border-radius: 8px; display: flex; gap: 12px; align-items: flex-start; color: #dd6b20; font-size: 0.8rem; line-height: 1.4; }
  .email-alert-box i { font-size: 1.1rem; margin-top: 2px; }
  
  .px-5 { padding-left: 30px !important; padding-right: 30px !important; }

  @media (max-width: 640px) {
    .setting-box { flex-direction: column; align-items: flex-start; }
    .box-action { width: 100%; }
    .box-action select { min-width: 100%; }
    .px-5 { padding-left: 20px !important; padding-right: 20px !important; }
  }
</style>